import { Button, Stack, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import Image from 'src/components/image';
import Iconify from 'src/components/iconify/iconify';
import PropTypes from 'prop-types';
import { useResponsive } from 'src/hooks/use-responsive';
import { getFormattedDateWithSlash } from './utility';
import { SCREENING_ERROR } from '../job-candidate/job-candidate-status-constants';
import DomainPieChart from './pie-chart';
import ProfileLoader from './loader';
import { CandidateProfileContext } from './context/candidate-profile-context';
import FallbackUI from './fallback-ui';

const useStyles = makeStyles(theme => ({
  elementsDiv: {
    paddingTop: '16px',
    gap: 12,
  },
  blueTagsDiv: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  blueTagsDivItem: {
    borderRadius: 4,
    padding: '4px 8px',
    background: theme.palette.background.primaryAccent2,
    color: theme.palette.content.accent1,
    margin: '0px 8px 8px 0px',
  },
  ExperienceItemDiv: {
    padding: '16px 12px',
    border: `1px solid ${theme.palette.border.primaryBlack3}`,
    borderRadius: 6,
  },
  workDuration: {
    padding: '4px 8px',
    borderRadius: 21,
    background: theme.palette.background.primaryGrey2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  disqualifiedDiv: {
    flexDirection: 'row',
    padding: '11.5px 16px',
    background: theme.palette.content.primaryRed2,
    color: theme.palette.background.primaryRed1,
    alignItems: 'center',
    gap: '10px',
  },
}));

const ExperienceItem = ({ item }) => {
  // hooks
  const classes = useStyles();
  const mdUp = useResponsive('up', 'md');

  // variables
  const workTags = item?.work_tags?.split(',');
  const startDate = getFormattedDateWithSlash(item?.start_date);
  const endDate = getFormattedDateWithSlash(item?.end_date);

  return (
    <Stack
      className={classes.ExperienceItemDiv}
      spacing={{
        xs: '10px',
        md: '8px',
      }}
    >
      <Typography
        variant="paragraph"
        sx={{ fontWeight: 600, marginBottom: '4px' }}
      >
        {item?.job_role}
      </Typography>
      <Stack spacing="4px" direction="row" alignItems="center" flexWrap="wrap">
        <Typography variant="caption">{item?.company_name}</Typography>
        {startDate && endDate && (
          <>
            <Typography variant="caption">&bull;</Typography>
            <Typography variant="caption">
              {startDate}-{endDate}
            </Typography>
          </>
        )}
        {item?.work_duration &&
          !item?.work_duration?.toLowerCase().includes('not') && (
            <>
              <Typography variant="caption">&bull;</Typography>
              <Stack className={classes.workDuration}>
                <Typography variant="caption">{item?.work_duration}</Typography>
              </Stack>
            </>
          )}
      </Stack>
      <Typography variant="caption" sx={{ lineHeight: 'normal' }}>
        {item?.work_summary}
      </Typography>
      <Stack className={classes.blueTagsDiv}>
        {workTags?.map((tag, ind) => (
          <Stack key={ind} className={classes.blueTagsDivItem}>
            <Typography
              variant={mdUp ? 'input' : 'caption'}
              sx={{ fontWeight: 500, fontSize: 12 }}
            >
              #{tag}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};

const EducationItem = ({ item }) => {
  // hooks
  const classes = useStyles();

  return (
    <Stack className={classes.ExperienceItemDiv}>
      {item?.degree && (
        <Typography
          variant="paragraph"
          sx={{ fontWeight: 600, marginBottom: '4px' }}
        >
          {item?.degree}
        </Typography>
      )}
      <Stack spacing="4px" direction="row">
        {item?.institution && (
          <Typography variant="caption">{item?.institution}</Typography>
        )}
        {item?.start_time && item?.end_time && (
          <>
            <Typography variant="caption">&bull;</Typography>
            <Typography variant="caption">
              {item?.start_time}-{item?.end_time}
            </Typography>
          </>
        )}
      </Stack>
      {item?.summary && (
        <Typography variant="caption" sx={{ lineHeight: 'normal' }}>
          {item?.summary}
        </Typography>
      )}
    </Stack>
  );
};

const chartColors = [
  '#002D69',
  '#2F9FF6',
  '#212121',
  '#3357FF',
  '#383838',
  '#5418EF',
  '#08944A',
];

const ProfileGroup = ({ jobId }) => {
  // hooks
  const classes = useStyles();
  const candidateDetails = useContext(CandidateProfileContext)?.obj;
  const isLoading = useContext(CandidateProfileContext)?.isLoading;
  const mdUp = useResponsive('up', 'md');

  // memo
  const resumeUrl = useMemo(
    () => candidateDetails?.candidate_info?.resume_url,
    [candidateDetails?.candidate_info?.resume_url],
  );

  // states
  const [profile, setProfile] = useState(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);

  // variables
  const jobCandidateStatus = candidateDetails?.screening_status;
  const personality = profile?.user_persona?.personality;
  const skills = profile?.user_persona?.skills;
  const summary = candidateDetails?.candidate_info?.profile_blob
    ?.split('\n\n')[4]
    .slice(8);
  const experiences = Object.values(
    profile === null || Object.keys(profile).length === 0
      ? {}
      : profile?.experience,
  );
  const education = Object.values(
    profile === null || Object.keys(profile).length === 0
      ? {}
      : profile?.education,
  );
  const chartData = profile?.user_persona?.domain_list?.map((item, index) => {
    let label = `${item?.domain_name} ${item?.total_exp}`;

    if (item?.total_exp === 1) label += ' year';
    else label += ' years';

    const obj = {
      id: index,
      value: item?.total_exp,
      name: label,
      fill: chartColors[index],
    };

    return obj;
  });

  const downloadResume = async _resumeUrl => {
    if (_resumeUrl) {
      window.open(_resumeUrl, '_blank');
    }
  };

  // effects
  useEffect(() => {
    if (candidateDetails?.candidate_info?.profile_json) {
      const parsedProfile = JSON.parse(
        candidateDetails?.candidate_info?.profile_json,
      );

      setProfile(parsedProfile);
    }
    if (!isLoading) {
      setIsProfileLoading(false);
    }
  }, [candidateDetails, isLoading]);

  return (
    <>
      {isProfileLoading ? (
        <ProfileLoader />
      ) : (
        candidateDetails &&
        Object.keys(candidateDetails).length !== 0 && (
          <>
            {profile && Object.keys(profile).length > 0 ? (
              <Stack spacing="20px">
                {profile && personality && (
                  <Stack className={classes.elementsDiv}>
                    <Typography
                      variant={mdUp ? 'subheading1' : 'subheading2'}
                      fontSize={{
                        xs: '16px',
                        md: '18px',
                      }}
                    >
                      Personality
                    </Typography>
                    <Stack className={classes.blueTagsDiv}>
                      {personality?.map((item, index) => (
                        <Stack key={index} className={classes.blueTagsDivItem}>
                          <Typography variant={mdUp ? 'input' : 'caption'}>
                            #{item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                )}
                {profile && skills && (
                  <Stack className={classes.elementsDiv}>
                    <Typography variant={mdUp ? 'subheading1' : 'subheading2'}>
                      Skills
                    </Typography>
                    <Stack className={classes.blueTagsDiv}>
                      {skills?.map((item, index) => (
                        <Stack key={index} className={classes.blueTagsDivItem}>
                          <Typography variant={mdUp ? 'input' : 'caption'}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                )}
                {summary && (
                  <Stack className={classes.elementsDiv}>
                    <Typography variant={mdUp ? 'subheading1' : 'subheading2'}>
                      Summary
                    </Typography>
                    <Typography
                      variant={mdUp ? 'input' : 'caption'}
                      sx={{ lineHeight: 'normal' }}
                    >
                      {summary}
                    </Typography>
                  </Stack>
                )}
                {chartData && chartData.length > 0 && (
                  <Stack className={classes.elementsDiv}>
                    <Typography
                      variant="subheading2"
                      fontSize={{
                        xs: '16px',
                        md: '18px',
                      }}
                    >
                      Domain
                    </Typography>
                    <DomainPieChart chartData={chartData} />
                  </Stack>
                )}
                {profile && experiences && experiences.length > 0 && (
                  <Stack className={classes.elementsDiv}>
                    <Typography variant={mdUp ? 'subheading1' : 'subheading2'}>
                      Experience
                    </Typography>
                    {experiences?.map((item, index) => (
                      <ExperienceItem key={index} item={item} />
                    ))}
                  </Stack>
                )}
                {profile && education && education.length > 0 && (
                  <Stack className={classes.elementsDiv}>
                    <Typography
                      variant="subheading2"
                      fontSize={{
                        xs: '16px',
                        md: '18px',
                      }}
                    >
                      Education
                    </Typography>
                    {education?.map((item, index) => (
                      <EducationItem key={index} item={item} />
                    ))}
                  </Stack>
                )}
                {jobCandidateStatus === SCREENING_ERROR && (
                  <Stack className={classes.disqualifiedDiv}>
                    <Image src="/assets/resume-error.svg" />
                    <Typography variant={mdUp ? 'subheading1' : 'subheading2'}>
                      Resume Parsing failed
                    </Typography>
                  </Stack>
                )}
              </Stack>
            ) : (
              <FallbackUI content="No data found" />
            )}
            {/* {resumeUrl ? (
              <Stack className={classes.elementsDiv}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => downloadResume(resumeUrl)}
                  //   size="small"
                  startIcon={<Iconify icon="tabler:download" />}
                >
                  Download Resume
                </Button>
              </Stack>
            ) : null} */}
          </>
        )
      )}
    </>
  );
};

export default ProfileGroup;

ProfileGroup.propTypes = {
  jobId: PropTypes.string,
};

ExperienceItem.propTypes = {
  item: PropTypes.object.isRequired,
};

EducationItem.propTypes = {
  item: PropTypes.object.isRequired,
};
